import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useSearch } from '../hooks/useSearch';
import { SearchFiltersPanel } from './SearchFilters';
import { SearchResultCard } from './SearchResultCard';
import type { SearchFilters, SearchHit } from '../types';

/**
 * Full search results page with filters sidebar and páginated results.
 * Reads the initial query from URL search params (?q=...).
 */
export function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const queryFromUrl = searchParams.get('q') || '';
  const { results, meta, isLoading, error, executeSearch } = useSearch();

  const [filters, setFilters] = useState<SearchFilters>({});
  const [showFilters, setShowFilters] = useState(!isMobile);

  // Execute search when URL query or filters change
  useEffect(() => {
    if (queryFromUrl) {
      executeSearch(queryFromUrl, filters, 1);
    }
  }, [queryFromUrl, filters, executeSearch]);

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      executeSearch(queryFromUrl, filters, page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [queryFromUrl, filters, executeSearch],
  );

  const handleFilterChange = useCallback((newFilters: SearchFilters) => {
    setFilters(newFilters);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters({});
  }, []);

  const handleResultClick = useCallback(
    (hit: SearchHit) => {
      switch (hit.type) {
        case 'user':
          setSearchParams({ q: hit.title });
          break;
        case 'trip':
        case 'pin':
        case 'photo':
          // Navigate to entity detail (placeholder)
          break;
      }
    },
    [setSearchParams],
  );

  return (
    <Box sx={{ display: 'flex', maxWidth: 1200, mx: 'auto', p: 2, gap: 3 }}>
      {/* Filters sidebar */}
      {showFilters && (
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : 280,
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            position: isMobile ? 'fixed' : 'sticky',
            top: isMobile ? 0 : 80,
            left: isMobile ? 0 : undefined,
            zIndex: isMobile ? 1200 : undefined,
            height: isMobile ? '100vh' : 'fit-content',
            maxHeight: isMobile ? undefined : 'calc(100vh - 100px)',
            overflow: 'auto',
          }}
        >
          <SearchFiltersPanel
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
          />
        </Paper>
      )}

      {/* Main content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header with query info and filter toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              Search results for "{queryFromUrl}"
            </Typography>
            {meta && (
              <Typography variant="body2" color="text.secondary">
                {meta.totalItems} result{meta.totalItems !== 1 ? 's' : ''} found
              </Typography>
            )}
          </Box>
          {isMobile && (
            <IconButton onClick={() => setShowFilters(!showFilters)} aria-label="Toggle filters">
              <FilterListIcon />
            </IconButton>
          )}
        </Box>

        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !error && results.length === 0 && queryFromUrl && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your search terms or filters.
            </Typography>
          </Box>
        )}

        {/* Results list */}
        {!isLoading && results.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {results.map((hit) => (
              <SearchResultCard key={`${hit.type}-${hit.id}`} hit={hit} onClick={handleResultClick} />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {meta && meta.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={meta.totalPages}
              page={meta.page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
